const sectionSelector = ".collection-seo-text__see-more";
const copySelector = ".collection-seo-text__copy";
const expandSelector = ".collection-seo-text__see-more-button--expand";
const collapseSelector = ".collection-seo-text__see-more-button--collapse";
const measuringClass = "collection-seo-text__copy--clamp";

// punctuation left dangling before the ellipsis reads as "word,..." so it is dropped
const trailingPunctuation = /[\s,.;:!?\-–—]+$/;

const states = new WeakMap();

function getState(wrapper) {
  if (!states.has(wrapper)) {
    const copy = wrapper.querySelector(copySelector);
    const expand = wrapper.querySelector(expandSelector);
    const collapse = wrapper.querySelector(collapseSelector);

    if (!copy || !expand || !collapse) {
      return null;
    }

    const state = {
      copy,
      expand,
      collapse,
      original: copy.cloneNode(true),
      expanded: false,
      width: 0,
    };

    expand.addEventListener("click", () => {
      state.expanded = true;
      render(state);
      collapse.focus();
    });

    collapse.addEventListener("click", () => {
      state.expanded = false;
      render(state);
      expand.focus();
    });

    states.set(wrapper, state);
  }

  return states.get(wrapper);
}

function getTextNodes(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];

  while (walker.nextNode()) {
    nodes.push(walker.currentNode);
  }

  return nodes;
}

function countWords(text) {
  return text.split(/\s+/).filter(Boolean).length;
}

// keeps the first `limit` words of the copy, drops everything after them, and returns the text
// node the copy now ends on so the button can follow it inline
function truncate(root, limit) {
  let remaining = limit;

  for (const node of getTextNodes(root)) {
    const words = countWords(node.textContent);

    if (words < remaining) {
      remaining -= words;
      continue;
    }

    const tokens = node.textContent.split(/(\s+)/);
    let kept = "";
    let seen = 0;

    for (const token of tokens) {
      if (seen === remaining) break;
      if (token.trim()) seen += 1;
      kept += token;
    }

    node.textContent = kept.replace(trailingPunctuation, "");

    let current = node;

    while (current && current !== root) {
      while (current.nextSibling) {
        current.nextSibling.remove();
      }

      current = current.parentNode;
    }

    return node;
  }

  return null;
}

function fits(copy) {
  return copy.scrollHeight <= copy.clientHeight + 1;
}

function showFull(state) {
  state.copy.replaceChildren(...state.original.cloneNode(true).childNodes);
}

function showTruncated(state, limit) {
  const clone = state.original.cloneNode(true);
  const end = truncate(clone, limit);

  state.copy.replaceChildren(...clone.childNodes);

  if (end) {
    end.after(state.expand);
  } else {
    state.copy.append(state.expand);
  }
}

function render(state) {
  const { copy, expand, collapse } = state;

  // park the expand button back outside the copy before the copy is rebuilt
  collapse.before(expand);
  expand.hidden = true;
  collapse.hidden = true;

  showFull(state);
  copy.classList.add(measuringClass);

  const overflows = !fits(copy);

  if (!overflows || state.expanded) {
    copy.classList.remove(measuringClass);
    collapse.hidden = !overflows;
    return;
  }

  expand.hidden = false;

  // the most whole words that still fit inside the clamp with the button after them
  let low = 0;
  let high = countWords(state.original.textContent);

  while (low < high) {
    const mid = Math.ceil((low + high) / 2);

    showTruncated(state, mid);

    if (fits(copy)) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }

  showTruncated(state, low);
  copy.classList.remove(measuringClass);
}

function syncSeeMore(wrapper, force = false) {
  const state = getState(wrapper);

  if (!state) {
    return;
  }

  const width = state.copy.clientWidth;

  // mobile browsers fire resize when the toolbar shows or hides, which doesn't change the wrap
  if (!width || (!force && width === state.width)) {
    return;
  }

  state.width = width;
  render(state);
}

function syncAll(root = document, force = false) {
  root.querySelectorAll(sectionSelector).forEach((wrapper) => syncSeeMore(wrapper, force));
}

function scheduleSync() {
  requestAnimationFrame(() => requestAnimationFrame(() => syncAll()));
}

scheduleSync();

if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(() => syncAll(document, true));
}

let resizeTimer = null;

window.addEventListener("resize", () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => syncAll(), 150);
});

document.addEventListener("shopify:section:load", (event) => syncAll(event.target, true));
